import React, { useState } from 'react';
import { Card, Collapse, Row, Col, Input, Button, Space, Table, Modal, Typography, Tag, AutoComplete, Form } from 'antd';
import { SearchOutlined, FilterOutlined, ReloadOutlined, CheckOutlined, InfoCircleOutlined, ShopOutlined, PlusOutlined } from '@ant-design/icons';
import ProdusForm from "../../Achizitii/components/ProdusForm"
import { useProduse } from '../../Achizitii/hooks/useProduse';
import { useAdaugaProduse } from '../hook/useAdaugaProduse';
import { useStateContext } from '../../../../../contexts/ContextProvider';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;
const { Panel } = Collapse;

// Componentă simplă pentru căutarea și selectarea produselor
const SearchProductModal = ({ pendingProduct, onSelectProduct, onCancel }) => {
  const [form] = Form.useForm();
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { token } = useStateContext();

  const handleSearch = async (value) => {
    if (!value || value.length < 2) {
      setSearchResults([]);
      return;
    }

    console.log('Căutare pentru:', value);
    setSearchLoading(true);
    try {
      const params = new URLSearchParams({
        token,
        page: 1,
        page_size: 10,
        search: value
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/produse?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Răspuns server:', data);
        console.log('Items găsite:', data.items?.length || 0);
        setSearchResults(data.items || []);
      } else {
        console.error('Eroare la căutarea produselor:', response.status);
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Eroare la căutarea produselor:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleProductSelect = (productId) => {
    const product = searchResults.find(p => p.id === productId);
    setSelectedProduct(product);
    form.setFieldsValue({ 
      id_produs: productId,
      product_qt: pendingProduct?.quantity || 1 
    });
  };

  const handleSubmit = (values) => {
    if (selectedProduct) {
      // Creăm obiectul produs cu datele necesare pentru ofertă
      const productForOferta = {
        ...selectedProduct,
        product_qt: values.product_qt,
        sale_price: values.sale_price || selectedProduct.pret_vanzare,
        bought_price: selectedProduct.furnizori?.[0]?.pret || 0
      };
      onSelectProduct(productForOferta);
    }
  };

  return (
    <div>
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="font-medium text-gray-900 mb-2">Produs solicitat:</div>
        <div className="bg-white p-3 rounded border">
          <div className="font-semibold text-gray-900">{pendingProduct?.product_name}</div>
          {pendingProduct?.description && (
            <div className="text-sm text-gray-600 mt-1">{pendingProduct.description}</div>
          )}
          <div className="text-xs text-gray-500 mt-2">
            Cantitate solicitată: <span className="font-medium">{pendingProduct?.quantity} buc</span>
          </div>
        </div>
      </div>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="id_produs"
          label="Caută produs existent"
          rules={[{ required: true, message: 'Selectează un produs' }]}
        >
          <AutoComplete
            placeholder="Caută produs după denumire sau SKU..."
            onSearch={handleSearch}
            onSelect={handleProductSelect}
            loading={searchLoading}
            options={(() => {
              const options = searchResults.map(produs => ({
                value: produs.id,
                label: (
                  <div className="flex items-center space-x-3 p-2">
                    {produs.poza_principala_url && (
                      <img 
                        src={`${process.env.REACT_APP_API_URL}${produs.poza_principala_url}`} 
                        alt={produs.denumire}
                        className="w-10 h-10 object-cover rounded"
                      />
                    )}
                    <div>
                      <div className="font-medium">{produs.denumire}</div>
                      <div className="text-xs text-gray-500">
                        SKU: {produs.sku_intern} | 
                        {produs.pret_vanzare && ` Preț: ${produs.pret_vanzare} MDL`}
                      </div>
                    </div>
                  </div>
                )
              }));
              console.log('Opțiuni AutoComplete construite:', options.length);
              return options;
            })()}
          />
        </Form.Item>

        {selectedProduct && (
          <>
            <div className="mb-4 p-3 bg-gray-50 rounded">
              <div className="text-sm font-medium mb-2">Produsul selectat:</div>
              <div className="flex items-center space-x-2">
                <img 
                  src={`${process.env.REACT_APP_API_URL}${selectedProduct.poza_principala_url}`} 
                  alt={selectedProduct.denumire}
                  style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '5px' }}
                />
                <div>
                  <div className="font-medium">{selectedProduct.denumire}</div>
                  <div className="text-xs text-gray-500">
                    SKU: {selectedProduct.sku_intern} | 
                    {selectedProduct.pret_vanzare && ` Preț: ${selectedProduct.pret_vanzare} MDL`}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        <div className="flex justify-end space-x-2">
          <Button onClick={onCancel}>Anulează</Button>
          <Button type="primary" htmlType="submit" disabled={!selectedProduct}>
            Atașează Produs
          </Button>
        </div>
      </Form>
    </div>
  );
};

const AdaugaProduse = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSearchModalVisible, setIsSearchModalVisible] = useState(false);
  const { createProdus } = useProduse();
  const { loading, newProducts, handleSearch, clearFilters, isFilterModalVisible, setIsFilterModalVisible, handleShowDetails, deleteProdusRequest, addProductToOferta } = useAdaugaProduse();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const navigate = useNavigate();

  const handleModalSubmit = async (values) => {
    let success;
    const newProduct = await createProdus(values);
    
    if (newProduct.id) {
      addProductToOferta(newProduct, selectedProduct.oferta_id, selectedProduct);
      deleteProdusRequest(selectedProduct); 
      setIsModalVisible(false);
    }
  };

  const handleSearchProduct = (product) => {
    setSelectedProduct(product);
    setIsSearchModalVisible(true);
  };

  const handleAttachExistingProduct = (existingProduct) => {
    if (existingProduct && selectedProduct) {
      addProductToOferta(existingProduct, selectedProduct.oferta_id, selectedProduct);
      deleteProdusRequest(selectedProduct);
      setIsSearchModalVisible(false);
    }
  };

  // Grupare produse după oferte
  const groupByOferta = (products) => {
    const grouped = {};
    products.forEach(product => {
      const ofertaKey = product.oferta_id;
      if (!grouped[ofertaKey]) {
        grouped[ofertaKey] = {
          oferta_id: ofertaKey,
          client_name: product.client_name,
          oferta_info: product.oferta_info,
          products: []
        };
      }
      grouped[ofertaKey].products.push(product);
    });
    return grouped;
  };

  // Calculare sumar pentru o ofertă
  const calculateOfertaSummary = (products) => {
    return {
      totalProducts: products.length,
      totalValue: products.reduce((sum, p) => sum + (p.sale_price || p.estimated_price || 0), 0),
      lastAddedDate: products.reduce((latest, p) => {
        const productDate = new Date(p.oferta_info?.date_added || p.oferta_info?.date_updated);
        return productDate > latest ? productDate : latest;
      }, new Date(0))
    };
  };

  // Sortare oferte după data adăugării (cele mai vechi primele)
  const sortOferte = (groupedOferte) => {
    return Object.values(groupedOferte).sort((a, b) => {
      const dateA = new Date(a.oferta_info?.date_added || a.oferta_info?.date_updated || 0);
      const dateB = new Date(b.oferta_info?.date_added || b.oferta_info?.date_updated || 0);
      return dateA - dateB;
    });
  };

  // Componentă pentru sumar ofertă
  const OfertaSummary = ({ summary }) => (
    <Space size="large">
      <Text strong>{summary.totalProducts} produse</Text>
      <Text strong>{summary.totalValue.toFixed(2)} MDL</Text>
      <Text type="secondary">
        Ultimul produs: {summary.lastAddedDate.toLocaleDateString('ro-RO')}
      </Text>
    </Space>
  );

  const grouped = groupByOferta(newProducts);
  const sortedOferte = sortOferte(grouped);

  const columns = [
    {
      title: 'Produs',
      dataIndex: 'product_name',
      key: 'product_name',
      render: (text, record) => (
        <div>
          <div>{text}</div>
          <small style={{ color: '#666' }}>{record.description}</small>
        </div>
      ),
    },
    {
      title: 'Cantitate',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
    },
    {
      title: 'Preț aproximativ',
      dataIndex: 'sale_price',
      key: 'sale_price',
      width: 120,
      render: (price, record) => {
        const displayPrice = price || record.estimated_price || 0;
        return `${displayPrice} MDL`;
      },
    },
    {
      title: 'Sursă',
      dataIndex: 'source',
      key: 'source',
      width: 80,
      render: (source) => (
        <Tag color={source === 'amo' ? 'blue' : 'green'}>
          {source === 'amo' ? 'AMO' : 'Manual'}
        </Tag>
      ),
    },
    {
      title: 'Acțiuni',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Button 
            type="primary" 
            icon={<SearchOutlined />}
            onClick={() => handleSearchProduct(record)}
            size="small"
          >
            Caută Produs
          </Button>
          <Button 
            icon={<PlusOutlined />}
            onClick={() => {setSelectedProduct(record); setIsModalVisible(true);}}
            size="small"
          >
            Adaugă Produs Nou
          </Button>
          <Button
            icon={<InfoCircleOutlined />}
            onClick={() => handleShowDetails(record)}
            size="small"
          />
        </Space>
      ),
    },
  ];

  return (
    <>
    <Card>
      <Row gutter={[16, 16]} className="mb-4">
        <Col xs={24} sm={12} md={8} lg={6}>
          <Input
            placeholder="Caută produs nou..."
            prefix={<SearchOutlined />}
            allowClear
            onSearch={handleSearch}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Space>
            <Button 
              icon={<FilterOutlined />}
              onClick={() => setIsFilterModalVisible(true)}
            >
              Filtre Avansate
            </Button>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={clearFilters}
            >
              Resetează
            </Button>
          </Space>
        </Col>
      </Row>

      {loading ? (
        <Table
          columns={columns}
          dataSource={[]}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      ) : (
        <Collapse>
          {sortedOferte.map((oferta) => {
            const summary = calculateOfertaSummary(oferta.products);
            return (
              <Panel
                header={
                  <Row justify="space-between" align="middle" style={{ width: '100%' }}>
                    <Col>
                      <Space>
                        <ShopOutlined />
                        <Text strong>{oferta.client_name}</Text>
                        <Tag color="blue">ID: {oferta.oferta_id}</Tag>
                        <Tag color="green">AMO ID: {oferta.oferta_info.amo_id}</Tag>
                        <Button type="link" size="small" onClick={() => navigate(`/dashboard/oferte/${oferta.oferta_id}`)}>Vezi Oferta</Button>
                      </Space>
                    </Col>
                    <Col>
                      <OfertaSummary summary={summary} />
                    </Col>
                  </Row>
                }
                key={oferta.oferta_id}
              >
                <Table
                  columns={columns}
                  dataSource={oferta.products}
                  rowKey="id"
                  pagination={false}
                />
              </Panel>
            );
          })}
        </Collapse>
      )}
    </Card>

    <Modal
    title={'Adaugă Produs Nou'}
    open={isModalVisible}
    onCancel={() => {
      setIsModalVisible(false);
    }}
    footer={null}
    width={800}
    destroyOnClose
    >
    <ProdusForm
      onSubmit={handleModalSubmit}
      onCancel={() => {
        setIsModalVisible(false);
      }}
    />
    </Modal>

    <Modal
      title="Caută Produs Existent"
      open={isSearchModalVisible}
      onCancel={() => {
        setIsSearchModalVisible(false);
        setSelectedProduct(null);
      }}
      footer={null}
      width={1000}
      destroyOnClose
    >
      {selectedProduct && (
        <SearchProductModal
          pendingProduct={selectedProduct}
          onSelectProduct={handleAttachExistingProduct}
          onCancel={() => {
            setIsSearchModalVisible(false);
            setSelectedProduct(null);
          }}
        />
      )}
    </Modal>

    </>
  );
};

export default AdaugaProduse; 